import AppDialogs from '@/components/AppDialogs';
import FuseLayout from '@fuse/core/FuseLayout';
import FuseSuspense from '@fuse/core/FuseSuspense';
import { SnackbarProvider } from 'notistack';
import themeLayouts from '@/components/theme-layouts/themeLayouts';
import FuseSettingsProvider from '@fuse/core/FuseSettings/FuseSettingsProvider';
import { I18nProvider } from '@i18n/I18nProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFnsV3';
import { enUS } from 'date-fns/locale/en-US';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { useMemo } from 'react';
import ErrorBoundary from '@fuse/utils/ErrorBoundary';
import Authentication from '@auth/Authentication';
import MainThemeProvider from '../contexts/MainThemeProvider';
import routes from '@/configs/routesConfig';
import AppContext from '@/contexts/AppContext';
import NavigationLoader from '@/components/NavigationLoader';
import SettingsLoader from './SettingsLoader';
import AccountTimeZoneProvider from '@/contexts/AccountTimeZoneProvider';
import DynamsoftScanListener from '@/components/DynamsoftScanListener';
import { initializeSyncfusion } from '../configs/syncfusionConfig';

initializeSyncfusion();

/**
 * The main App component.
 */
function App() {
  const val = useMemo(
    () => ({
      routes
    }),
    [routes]
  );

  return (
    <ErrorBoundary>
      {/* Providers and shared layout components can suspend outside the route outlet. */}
      <FuseSuspense>
        <AppContext.Provider value={val}>
          {/* Date Picker Localization Provider */}
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={enUS}>
            <Authentication>
              <FuseSettingsProvider>
                <SettingsLoader>
                  <I18nProvider>
                    <MainThemeProvider>
                      <SnackbarProvider
                        maxSnack={5}
                        anchorOrigin={{
                          vertical: 'bottom',
                          horizontal: 'right'
                        }}
                        classes={{
                          containerRoot: 'bottom-0 right-0 mb-52 md:mb-68 mr-8 lg:mr-80 z-99'
                        }}
                      >
                        <AccountTimeZoneProvider>
                          {/* Load navigation dynamically */}
                          <NavigationLoader />

                          {/* 🚀 Dynamsoft Scan Listener - listens for AI Agent scan commands */}
                          <DynamsoftScanListener
                            hideUI
                            onScanTriggered={() => {}}
                            onScanComplete={() => {}}
                          />

                          {/* Main Layout */}
                          <FuseLayout layouts={themeLayouts} />
                          <AppDialogs />
                        </AccountTimeZoneProvider>
                      </SnackbarProvider>
                    </MainThemeProvider>
                  </I18nProvider>
                </SettingsLoader>
              </FuseSettingsProvider>
            </Authentication>
          </LocalizationProvider>
        </AppContext.Provider>
      </FuseSuspense>
    </ErrorBoundary>
  );
}

export default App;
